import { useEffect, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { trpc } from '../lib/trpc';
import { BottomSheet } from './BottomSheet';
import { BrandSwitch } from './BrandSwitch';
import { Button } from './Button';
import { Select } from './Select';
import { useIsDesktop } from '../hooks/useIsDesktop';
import {
  ACCOUNT_TAX_SUBTYPE_OPTIONS,
  getAccountTaxSubtypeLabel,
} from '../lib/accountTaxSubtypes';
import { webPrivacyProps } from '../lib/privacy';

interface ReviewAccount {
  id: string;
  name: string;
  subtype: string | null;
  institutionName: string | null;
  needsTaxSubtypeReview: boolean;
  excludeFromNetWorthAndFire: boolean;
}

interface AccountTaxSubtypeReviewSheetProps {
  visible: boolean;
  accounts: ReviewAccount[];
  onClose: () => void;
  onSaved?: () => void;
}

export function AccountTaxSubtypeReviewSheet({
  visible,
  accounts,
  onClose,
  onSaved,
}: AccountTaxSubtypeReviewSheetProps) {
  const isDesktop = useIsDesktop();
  const reviewMutation = trpc.accounts.reviewTaxSubtypes.useMutation();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [exclusions, setExclusions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!visible) return;

    setDrafts(
      Object.fromEntries(
        accounts.map((account) => [
          account.id,
          account.subtype?.trim().toLowerCase() || 'unknown',
        ]),
      ),
    );
    setExclusions(
      Object.fromEntries(
        accounts.map((account) => [account.id, account.excludeFromNetWorthAndFire]),
      ),
    );
  }, [accounts, visible]);

  const handleSave = async () => {
    await reviewMutation.mutateAsync({
      accounts: accounts.map((account) => ({
        id: account.id,
        subtype: drafts[account.id] ?? 'unknown',
        excludeFromNetWorthAndFire: exclusions[account.id] ?? false,
      })),
    });
    await onSaved?.();
    onClose();
  };

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      title="Review account tax labels"
      variant={isDesktop ? 'centered' : 'bottom'}
    >
      <ScrollView className="px-5 py-4">
        <Text className="text-sm text-gray-600 dark:text-gray-300">
          Brokerage connections do not always tell us whether an account is taxable, pre-tax, or Roth.
          Confirming these labels keeps your FIRE and tax projections honest.
        </Text>

        <View className="mt-4 gap-4">
          {accounts.map((account) => (
            <View
              key={account.id}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4"
            >
              <Text
                className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                {...webPrivacyProps('private')}
              >
                {account.name}
              </Text>
              <Text
                className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                {...webPrivacyProps('private')}
              >
                {account.institutionName ?? 'Connected account'}
                {account.subtype
                  ? ` · Current guess: ${getAccountTaxSubtypeLabel(account.subtype)}`
                  : ''}
              </Text>

              <Select
                className="mt-3"
                label="Account type for taxes"
                modalTitle={`Tax label for ${account.name}`}
                value={drafts[account.id] ?? 'unknown'}
                options={[...ACCOUNT_TAX_SUBTYPE_OPTIONS]}
                onChange={(value) =>
                  setDrafts((current) => ({ ...current, [account.id]: value }))
                }
              />

              <View className="mt-4 flex-row items-start justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-3">
                <View className="flex-1">
                  <Text className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Exclude from net worth and FIRE
                  </Text>
                  <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Keep this account visible, but leave it out of your balance sheet totals and FIRE projections.
                  </Text>
                </View>
                <BrandSwitch
                  value={exclusions[account.id] ?? false}
                  onValueChange={(value) =>
                    setExclusions((current) => ({ ...current, [account.id]: value }))
                  }
                  accessibilityLabel="Exclude from net worth and FIRE"
                />
              </View>
            </View>
          ))}
        </View>

        <View className="mt-5 gap-3 pb-4">
          <Button
            testID="account-tax-review-save-button"
            accessibilityLabel="Save account tax labels"
            onPress={handleSave}
            loading={reviewMutation.isPending}
          >
            Save labels
          </Button>
          <Button
            testID="account-tax-review-skip-button"
            accessibilityLabel="Skip account tax label review"
            variant="secondary"
            onPress={onClose}
            disabled={reviewMutation.isPending}
          >
            Skip for now
          </Button>
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            You can update these later from Balance Sheet if you are not sure right now.
          </Text>
        </View>
      </ScrollView>
    </BottomSheet>
  );
}
